import { createSelector } from "@reduxjs/toolkit";
import sortBy from "lodash/sortBy";

import { isFullLibrary } from "features/media-library";
import { isEditorUploadsCollection } from "utils/cesdk-editor-collection";

import { getEntities } from "./selectorUtils";
import { getPageSize, getUser } from "./commonSelectors";

const COLLECTION_ENTITY = "collections";
const COLLECTION_ENTRY_ENTITY = "collectionEntries";
const COLLECTION_USER_ENTITY = "collectionUsers";
const INSTAGRAM_HASHTAGS = "instagramHashtags";

// View
export const getLibraryInitialized = (state) =>
  state.mediaLibraryView.initialized;
export const getLibraryInitError = (state) => state.mediaLibraryView.initError;
export const getSelectAll = (state) =>
  state.entities[COLLECTION_ENTRY_ENTITY].allSelected;
export const getMediaLibraryFilters = (state) => state.mediaLibraryView.filters;
export const getIsMediaLibraryFiltered = (state) => {
  const filters = state.mediaLibraryView.filters;
  if (filters.name) {
    return true;
  }
  if (filters.tags && filters.tags.length > 0) {
    return true;
  }
  if (filters.description) {
    return true;
  }
  if (filters.sourceName) {
    return true;
  }
  if (filters.favorite) {
    return true;
  }
  if (filters.mediaType) {
    return true;
  }
  if (filters.usage) {
    return true;
  }
};

export const getFiltersVisible = (state) =>
  state.mediaLibraryView.filtersVisible;
export const getPage = (state) => state.mediaLibraryView.page;
export const getLayout = (state) => state.mediaLibraryView.layout;

// Assets
/**
 * SKE-4712. Memoized because `getEntities` builds a NEW array on every call
 * (`selectorUtils.ts:37-57`), so unmemoized this returned a fresh reference each
 * time and react-redux warned "Selector … returned a different result when
 * called with the same parameters" on the Library page, re-rendering every
 * consumer on unrelated store updates.
 *
 * This is the selector that was actually firing. The minified build reports the
 * mangled name, and the staging console said `Selector FE`; `FE` maps to
 * `e => (e => getEntities(e, COLLECTION_ENTRY_ENTITY))(e)` in the deployed
 * `store-chunk`, which is this pair. `getCollections` minifies to `BE` in the
 * same bundle, so despite having the identical shape it was NOT the offender
 * and is deliberately left alone. It is read by about ten components and
 * models, and memoizing it changes referential identity well outside the
 * Library page.
 *
 * Keyed off the raw `byId` and `allIds` slices rather than off `getEntities`,
 * for the same reason `getSelectedAssets` below is: passing a function that
 * builds a new array as an input selector memoizes nothing.
 */
export const getCollectionEntries = createSelector(
  (state) => state.entities[COLLECTION_ENTRY_ENTITY]?.byId,
  (state) => state.entities[COLLECTION_ENTRY_ENTITY]?.allIds,
  /**
   * `any[]` deliberately, to preserve the shape callers already had. The old
   * body returned `getEntities(state, …)` on an untyped `state`, which made the
   * result `any`; memoizing gave it a concrete type and broke three call sites
   * in `AssetGridNew.tsx` that destructure entries without annotating. Typing
   * the entries properly is worth doing, but it is a different change.
   *
   * @returns {any[]}
   */
  (byId, allIds) => (allIds ?? []).map((id) => byId?.[id]).filter(Boolean)
);
export const getCollectionAssets = (state) => getCollectionEntries(state);
export const getSelectedAssetIds = (state) =>
  state.entities[COLLECTION_ENTRY_ENTITY].selectedIds;

export const getSelectedAssets = createSelector(
  (state) => state.entities[COLLECTION_ENTRY_ENTITY].byId,
  getSelectedAssetIds,
  (byId, ids) => ids.map((id) => byId[id]).filter(Boolean)
);

export const getTotalAssets = (state) =>
  state.entities[COLLECTION_ENTRY_ENTITY].total;

// Users
export const getCollectionUsers = (state) =>
  getEntities(state, COLLECTION_USER_ENTITY);

// Collections
export const getIsLoadingCollections = (state) =>
  state.entities[COLLECTION_ENTITY].loading;

/**
 * Get all visible collections (excludes Editor Uploads collection).
 *
 * NOT memoized, deliberately, and SKE-4712 explains why it was left alone.
 *
 * It has the same shape as `getCollectionEntries` above — `sortBy` then one or
 * two `filter` passes build a new array every call — but it is NOT the selector
 * that was warning. The staging console named `Selector FE`, and in the deployed
 * `store-chunk` this function minifies to `BE` while `FE` is the collection
 * entries pair. So the warning belongs above, not here.
 *
 * Left alone because it is read by about ten components and models, including
 * the media picker and an Inbox modal. Memoizing it changes referential identity everywhere, so
 * every consumer with a `[collections]` effect dependency starts firing less
 * often. That is a real behaviour change well outside the Library page, and it
 * wants its own change with its own review rather than riding along with a fix
 * for a different selector.
 *
 * @param {Object} state - Redux state
 * @returns {import("api/media-library/types").MediaCollection[]} Visible collections
 */
export const getCollections = (state) => {
  const user = getUser(state);
  /** @type {import("api/media-library/types").MediaCollection[]} */
  const collections = sortBy(getEntities(state, COLLECTION_ENTITY), ["name"]);
  // Filter out Editor Uploads collection (managed by ceSDK editor)
  const visibleCollections = collections.filter(
    (collection) => !isEditorUploadsCollection(collection)
  );
  return isFullLibrary(user)
    ? visibleCollections
    : visibleCollections.filter((collection) => collection.isSystemInbox);
};

export const getSelectedCollections = (state) =>
  state.entities.collections.selectedIds;

export const getSelectedCollection = (state) => {
  const selectedIds = getSelectedCollections(state);

  const selectedCollections = getEntities(
    state,
    COLLECTION_ENTITY,
    selectedIds
  );

  // For now collections would only ever have one collection selected
  return selectedCollections.length > 0 ? selectedCollections[0] : null;
};
export const getCollectionsError = (state) =>
  state.entities[COLLECTION_ENTITY].error;

export const getLibraryUsage = (state) => state.mediaLibraryView.libraryUsage;

// Tags
export const getTags = (state) => state.entities.collections.tags;

export const getMediaLibraryPageSize = (state) => {
  const user = getUser(state);
  return isFullLibrary(user) ? (getPageSize(state) ?? 25) : 20;
};
